import { ExpandedFiltersProps } from "./ExpandedFilters";
import { PropsWithChildren, useEffect, useState, useRef } from "react";
import ExpandedFilters from "./ExpandedFilters";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Button from "../Button";

type ExpandedFiltersWrapperProps = {
  filters?: Record<string, boolean>;
  onClearAll: () => void;
} & ExpandedFiltersProps;

// Constants for calculating filter overflow
// ITEM_WIDTH matches regular filter max-w (200px)
const ITEM_WIDTH = 200;
// ITEM_GAP matches the gap-3 (12px) used in TableControls
const ITEM_GAP = 12;
// GAP is reserved space for "Filters" button + "Clear All" button + buffer for one DateSelect being 60px wider
const GAP = 280;

const getActiveFilters = (items: any[], filters: Record<string, boolean>) => {
  const trueKeys = Object.keys(filters).filter((key) => filters[key] === true);
  return items.filter((item) => {
    const name = item.name || item.component?.props?.name || item.component?.props?.["data-name"];
    return trueKeys.some((key) => key === name);
  });
};

export default function ExpandedFiltersWrapper(props: PropsWithChildren<ExpandedFiltersWrapperProps>) {
  const { items, filters, onClearAll } = props;
  const [menuBarWidth, setMenuBarWidth] = useState<number>(0);
  const { isCompactLayout } = useIsCompactLayout();

  const menuBarRef = useRef<HTMLDivElement>(null);
  const menuBar = document.getElementById("menu-bar");
  const { width } = useWindowSize();
  useEffect(() => {
    setMenuBarWidth(menuBarRef.current?.offsetWidth ?? 0);
  }, [menuBar, width]);

  const filteredAllItems = getActiveFilters(items, filters || {});
  const visibleItemsCount = Math.floor((menuBarWidth - GAP) / (ITEM_WIDTH + ITEM_GAP));
  const visibleItems = items.slice(0, visibleItemsCount > 0 ? visibleItemsCount : 0);

  const hiddenItems = items.slice(visibleItems.length);
  const filteredHiddenItems = getActiveFilters(hiddenItems, filters || {});

  return (
    <div className="sm:flex-1" ref={menuBarRef}>
      <div className="flex flex-row items-center gap-3">
        {visibleItems.map((item, index) => (
          <div key={index}>{item.wrappedComponent || item.component}</div>
        ))}
        <ExpandedFilters
          {...props}
          items={hiddenItems.concat(
            isCompactLayout
              ? [
                  {
                    component: (
                      <Button variant="text" onClick={onClearAll}>
                        Reset All
                      </Button>
                    )
                  }
                ]
              : []
          )}
          activeFilters={filteredHiddenItems.length}
        />
        {!isCompactLayout && filteredAllItems.length > 0 && (
          <div data-testid="clear-all-button">
            <Button variant="text" onClick={onClearAll}>
              Reset All
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
